import { useEffect, useState } from "react";

/**
 * DeathCinematic — brief red-ember fade + fatal-blow hold before the Game Over
 * panel appears. Fires once on death, then calls onComplete().
 *
 * Timings:
 *   0.00s  — screen begins fading to a deep ember red (radial gradient)
 *   0.35s  — heartbeat pulse (2 beats) + "FALLEN" typography wipes in
 *   1.10s  — hero portrait flash desaturates to greyscale
 *   1.90s  — fade out to reveal the Game Over screen underneath
 *   2.00s  — onComplete() fires, cinematic unmounts
 *
 * Respects `prefers-reduced-motion` — skips to onComplete() immediately.
 */
export default function DeathCinematic({ open, portraitUrl, className, onComplete }) {
  const [phase, setPhase] = useState("hidden"); // hidden | fadein | hold | fadeout

  useEffect(() => {
    if (!open) { setPhase("hidden"); return; }
    // Reduced-motion users get an instant transition to the game-over screen
    if (typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      onComplete?.();
      return;
    }
    setPhase("fadein");
    const t1 = setTimeout(() => setPhase("hold"),    350);
    const t2 = setTimeout(() => setPhase("fadeout"), 1900);
    const t3 = setTimeout(() => { setPhase("hidden"); onComplete?.(); }, 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [open, onComplete]);

  if (!open || phase === "hidden") return null;

  const isFadeout = phase === "fadeout";

  return (
    <div
      className="fixed inset-0 z-[70] pointer-events-none"
      data-testid="death-cinematic"
      style={{
        opacity: isFadeout ? 0 : 1,
        transition: "opacity 400ms ease-out",
      }}
    >
      {/* Red-ember radial fill — burns in from the center */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(226,88,34,0.35) 0%, rgba(90,10,4,0.85) 40%, rgba(15,3,2,0.98) 100%)",
          animation: "death-cine-ignite 1.6s ease-out both",
        }}
      />

      {/* Grain / smoke overlay for texture */}
      <div
        className="absolute inset-0 mix-blend-overlay opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      {/* Center stack: greyscale portrait + FALLEN header + heartbeat pulse */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 text-center px-6">
        {portraitUrl && (
          <div
            className="relative"
            style={{
              width: 220, height: 220,
              animation: "death-cine-heartbeat 0.9s ease-in-out 2",
            }}
          >
            <img
              src={portraitUrl}
              alt=""
              className="w-full h-full object-cover"
              style={{
                filter: "grayscale(100%) brightness(0.75) contrast(1.10)",
                borderRadius: 4,
                boxShadow: "0 0 60px rgba(226,88,34,0.6), inset 0 0 40px rgba(0,0,0,0.85)",
              }}
            />
            {/* Blood vignette */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, transparent 55%, rgba(138,3,3,0.55) 100%)",
              }}
            />
          </div>
        )}

        <div style={{ animation: "death-cine-fallen 0.7s cubic-bezier(0.20,0.90,0.30,1.00) 0.35s both" }}>
          <div className="text-[11px] tracking-[0.6em]" style={{ color: "#e25822", opacity: 0.85 }}>
            — {(className || "Wanderer").toUpperCase()} —
          </div>
          <h1
            className="font-heading italic mt-2"
            style={{
              fontSize: "clamp(4rem, 12vw, 8rem)",
              lineHeight: 1,
              color: "#f0e6d2",
              textShadow: "0 0 30px rgba(226,88,34,0.85), 0 4px 24px rgba(0,0,0,0.9)",
              letterSpacing: "0.03em",
            }}
          >
            FALLEN
          </h1>
          <div
            className="mx-auto mt-4 h-[2px]"
            style={{
              width: 180,
              background: "linear-gradient(90deg, transparent, #d4af37, transparent)",
              animation: "death-cine-line 0.6s ease-out 0.55s both",
            }}
          />
          <div className="text-[10px] tracking-[0.5em] ash-text mt-4" style={{ opacity: 0.85 }}>
            THE EMBER REMEMBERS
          </div>
        </div>
      </div>

      <style>{`
        @keyframes death-cine-ignite {
          0%   { opacity: 0; transform: scale(1.05); }
          50%  { opacity: 1; }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes death-cine-heartbeat {
          0%,100% { transform: scale(1);    filter: grayscale(100%) brightness(0.75); }
          25%     { transform: scale(1.045); filter: grayscale(100%) brightness(1.10); }
          50%     { transform: scale(1);     filter: grayscale(100%) brightness(0.75); }
        }
        @keyframes death-cine-fallen {
          0%   { opacity: 0; transform: translateY(24px) scale(0.98); letter-spacing: 0.5em; }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0)    scale(1);    letter-spacing: 0.03em; }
        }
        @keyframes death-cine-line {
          0%   { transform: scaleX(0); opacity: 0; }
          100% { transform: scaleX(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
